
import * as React from "react"
import { cn } from "@/lib/utils"
import { ImageIcon, FileVideoIcon, XIcon } from "lucide-react"
import { Button } from "./button"

export interface FileInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  preview?: boolean
  onFilesSelected?: (files: File[]) => void
  onFileRemove?: (index: number) => void
  acceptedFileTypes?: string
  maxFiles?: number
  selectedFiles?: File[]
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ className, preview = true, onFilesSelected, onFileRemove, acceptedFileTypes, maxFiles = 5, selectedFiles = [], ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files
      if (!fileList) return
      
      const filesArray = Array.from(fileList)
      const totalFiles = selectedFiles.length + filesArray.length
      
      if (maxFiles && totalFiles > maxFiles) {
        alert(`You can only upload up to ${maxFiles} files.`)
        return
      }
      
      onFilesSelected?.(filesArray)
      
      // Reset input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = ""
      }
    }
    
    const getFilePreview = (file: File) => {
      if (file.type.startsWith("image/")) {
        return URL.createObjectURL(file)
      }
      return null
    }
    
    const getFileIcon = (file: File) => {
      if (file.type.startsWith("image/")) {
        return <ImageIcon className="h-5 w-5" />
      } else if (file.type.startsWith("video/")) {
        return <FileVideoIcon className="h-5 w-5" />
      }
      return null
    }
    
    return (
      <div className={className}>
        <div className="flex items-center justify-center w-full">
          <label
            htmlFor={props.id}
            className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {props.accept?.includes("image") ? <ImageIcon className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" /> : <FileVideoIcon className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" />}
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {props.accept?.includes("image") ? "PNG, JPG or GIF" : "MP4, MOV or WebM"} (max {maxFiles} files)
              </p>
            </div>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              {...props}
            />
          </label>
        </div>

        {preview && selectedFiles.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative group">
                {file.type.startsWith("image/") ? (
                  <div className="relative h-24 rounded overflow-hidden bg-gray-200 dark:bg-gray-700">
                    <img
                      src={getFilePreview(file) || ""}
                      alt={file.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="relative h-24 rounded overflow-hidden flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                    {getFileIcon(file)}
                    <span className="text-xs mt-1 max-w-full px-2 truncate">{file.name}</span>
                  </div>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onFileRemove?.(index)}
                >
                  <XIcon className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
)
FileInput.displayName = "FileInput"

export { FileInput }
